import type { TokenMap } from "../../tokens";

/**
 * Default skin — "dusk": an evening-felt idiom rendered in a soft,
 * rounded language. The page base is a warm plum-ink rather than a
 * flat near-black, surfaces carry generous radii, and depth comes from
 * a solid edge under each shape plus one wide ambient shadow — never a
 * gloss gradient. A light variant of the same structure ships beside
 * it and is picked in settings.
 *
 * The playing cards are deliberately excluded from all of it: their
 * face, back and radius are pinned and must not move with the chrome.
 *
 * The felt-theme picker (forest / indigo / crimson / slate) remaps the
 * felt + rail pair through `html[data-felt-theme]` and leaves the
 * rest of the palette alone, so the affordance vocabulary does not
 * shift between picks.
 */
export const DEFAULT_TOKENS: TokenMap = {
  // ---- text ----
  // Three steps, all clearing WCAG AA (4.5:1) on every surface they
  // are used against — asserted in tests/a11y/tokens.contrast.test.ts.
  "--color-text-primary": "#f2f0f7",
  "--color-text-secondary": "#a49dba",
  "--color-text-tertiary": "#8b849e",

  // ---- chrome ----
  "--color-bg-base": "#14131b",
  "--color-surface": "#1e1c28",
  "--color-surface-2": "#282536",
  "--color-line": "#322e42",
  "--color-button-bg": "rgba(255, 255, 255, 0.08)",
  "--color-button-bg-hover": "rgba(255, 255, 255, 0.12)",
  "--color-button-border": "rgba(255, 255, 255, 0.18)",
  // Gold — pot, hero stack, hand strength, the dealer button.
  "--color-accent": "#f0c674",
  // Periwinkle — identity, not money. Avatar fills and the caller's
  // position pill; gold stays reserved for chips so the two never
  // compete for the same read.
  "--color-identity": "#8b7bf5",
  "--color-identity-soft": "#a99bff",
  "--color-identity-bg": "#2c2748",
  // Amber, kept off the accent so a green button cannot swallow its
  // own focus indicator.
  "--focus-ring-color": "#fcd34d",

  // ---- table surface ----
  // Near-black forest so the felt does not compete with the page base.
  "--color-felt": "#2f5a44",
  "--color-felt-rail": "#20402f",
  "--color-card-front": "#fbf7ec",
  "--color-card-back": "#1a1f2e",
  "--color-card-back-grid": "#2c3553",

  // ---- felt theme variants ----
  "--color-felt-forest": "#2f5a44",
  "--color-felt-forest-rail": "#20402f",
  "--color-felt-indigo": "#2f3566",
  "--color-felt-indigo-rail": "#1c1f3d",
  "--color-felt-crimson": "#5c2836",
  "--color-felt-crimson-rail": "#3a1722",
  "--color-felt-slate": "#2c3140",
  "--color-felt-slate-rail": "#1c2029",

  // ---- action-bar accent ----
  // The single re-themeable hook for the in-game bet/raise CTA and the
  // sizing controls. Kept apart from the gold `--color-accent` so the
  // table chrome's identity survives an action-bar re-skin.
  "--color-action-accent": "#6dbb63",
  "--color-action-accent-hover": "#8ed07f",
  "--color-action-accent-deep": "#4d9145",
  // Ink on the filled primary. White on this green reads 2.35:1, so
  // the label is the page's own surface ink instead — 7.4:1, and it
  // ties the one filled button back to the chrome around it.
  "--color-action-accent-fg": "#1e1c28",

  // ---- action colours ----
  // One triple per action, read by the action bar, the seat bet chip
  // and the action log alike, so a colour always means one thing.
  // Folding is not destructive — it is the quiet way out of a hand.
  // It reads NEUTRAL here; the red is spent on the two actions you
  // cannot take back (all-in, leaving the table).
  "--color-action-fold-fg": "#a49dba",
  "--color-action-fold-bg": "#282536",
  "--color-action-fold-border": "#312d40",
  "--color-action-check-fg": "#a9bde4",
  "--color-action-check-bg": "#232a3d",
  "--color-action-check-border": "#333d5b",
  "--color-action-call-fg": "#8ed07f",
  "--color-action-call-bg": "#1e3324",
  "--color-action-call-border": "#2f4d38",
  "--color-action-bet-fg": "#f0c674",
  "--color-action-bet-bg": "#332a16",
  "--color-action-bet-border": "#5c4d24",
  "--color-action-allin-fg": "#f0837e",
  "--color-action-allin-bg": "#3a1f22",
  "--color-action-allin-border": "#25161a",

  // ---- depth ----
  // Half of the soft-3D language. The other half is per-shape: a solid
  // edge in a darker mix of the shape's own fill, written at the rule
  // (`0 Npx 0 color-mix(...)`) because it has to answer to that fill.
  // Together they read as clay; a vertical gradient reads as 2010 gloss.
  "--shadow-ambient": "0 12px 26px rgba(0, 0, 0, 0.45)",
  // A modal floats further off the page than a panel does, so it casts
  // further. Six blocking overlays had written this same value by hand,
  // one character apart from each other.
  "--shadow-elevated": "0 16px 48px rgba(0, 0, 0, 0.6)",

  // ---- scrim ----
  // What a blocking overlay lays over the page. Tinted toward the base
  // background rather than pure black: neutral black over a warm-dark
  // ground reads as a hole punched in the screen. The six overlays that
  // predate this token each chose their own opacity — 0.72 five times
  // and 0.78 once, which is a difference nobody decided on.
  "--scrim": "rgba(10, 9, 14, 0.76)",

  // ---- typography ----
  // Webfonts are self-hosted via @fontsource (see main.tsx); the stack
  // still names system fallbacks because no Korean webfont is bundled
  // and Hangul always renders through the OS face.
  // A ROUNDED grotesque for Latin. Hangul falls through to the
  // platform face by design — a bundled rounded Hangul was tried and
  // the OS face read better here, so the stack stays short.
  "--font-family":
    "Nunito, Pretendard, -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  "--font-family-mono":
    "'JetBrains Mono', 'SF Mono', Menlo, Consolas, ui-monospace, monospace",
  // Eight steps. Every `font-size` in the skin picks one of these; a
  // literal in a stylesheet is a bug the CSS invariant test catches.
  "--font-size-2xs": "0.65rem",
  "--font-size-xs": "0.72rem",
  "--font-size-sm": "0.78rem",
  "--font-size-md": "0.85rem",
  "--font-size-lg": "0.95rem",
  "--font-size-xl": "1.05rem",
  "--font-size-2xl": "1.35rem",
  "--font-size-3xl": "1.8rem",

  // ---- geometry ----
  // Radii by how round a thing is, not by what it is: chips and
  // badges, controls, cards, panels, sheets and dialogs, the felt
  // rail, and fully-rounded pills. Circles stay `50%` in the
  // stylesheet — that is a shape, not a step on this ladder.
  // The playing card keeps its own radius. The chrome ladder sits
  // where the direction put it — action buttons 20, pods 20, the hero
  // plate 26, the table 40 — while the card face, back and 8px corner
  // are frozen by decision. The card looking slightly sharper than
  // everything around it is the intended consequence.
  "--radius-chip": "14px",
  "--radius-button": "14px",
  "--radius-playing-card": "8px",
  "--radius-card": "20px",
  "--radius-panel": "20px",
  "--radius-surface": "26px",
  "--radius-rail": "40px",
  "--radius-pill": "999px",
  "--spacing-table-pad": "24px",
};
